import { DirectionalHint, TooltipDelay } from "@fluentui/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import CustomToolTipHost from "../../../../../../components/CustomToolTipHost/CustomToolTipHost";
import DropDown from "../../../../../../components/SVGComponents/Tools/DropDown";
import HPIcon from "../../../../../../components/SVGComponents/Tools/HPIcon";
import studyviewerContext from "../../../../../../context/studyviewer/StudyviewerContext";
import ViewportContext from "../../../../../../context/studyviewer/ViewportContext";
import themeContext from "../../../../../../context/theme/ThemeContext";
import { toolBarFlex, toolStyle, toolTipStyles } from "../../../../styles/styles";
import toastContext from "../../../../../../context/toast/ToastContext";
import { AuthRoutes, TOAST_TYPE } from "../../../../../../constants/Constants";
import { hpKey } from "../../../../../../helpers/hangingProtocol/hpIdentity";
import HangingProtocolMenu from "./HangingProtocolMenu";

const HPTool = () => {
  const { t } = useTranslation();
  const { theme } = useContext(themeContext);
  const { showToastv2 } = useContext(toastContext);
  const [showDropDown, setShowDropDown] = useState(false);

  const [hovered, setHovered] = useState(false);
  const [dropDownHovered, setDropDownHovered] = useState(false);
  const { exitHangingProtocol, reapplyHangingProtocol } = useContext(ViewportContext);
  const { isHPApplied, hpCandidates, appliedHp, setAppliedHp } = useContext(studyviewerContext);
  const dropDownRef = useRef(null);

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const handleMouseOver = () => {
    setHovered(true);
  };

  const onHpClick = (candidate) => {
    setShowDropDown(false);
    if (!candidate?.hp) return;
    setAppliedHp(candidate.hp);
  };

  const handleReset = () => {
    setShowDropDown(false);
    exitHangingProtocol();
  };

  const handleManage = () => {
    setShowDropDown(false);
    window.open(AuthRoutes.HANGINGPROTOCOLV2, "_blank", "noopener");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setShowDropDown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // The icon toggles the hanging protocol: applied -> back to the default 1x1
  // layout, not applied -> re-hang the last (or best matching) protocol.
  const handleHpIconClick = () => {
    setShowDropDown(false);
    if (isHPApplied) {
      exitHangingProtocol();
      return;
    }
    if (!reapplyHangingProtocol()) {
      showToastv2(TOAST_TYPE.INFO, t("hanging-protocol.no-suitable-protocol"));
    }
  };

  return (
    <>
      {isMobile ? (
        <>
          <div style={toolStyle}>
            <>
              <div
                style={{
                  height: "65%",
                  width: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className={isMobile ? "highlight-tool-mobile" : "highlight-tool"}>
                  <HPIcon
                    color={hovered ? theme.palette.activeTool : theme.palette.inactiveTool}
                  />
                </div>
              </div>
              <span
                style={{
                  fontSize: "9px",
                  lineHeight: "100%",
                  fontWeight: "400",
                  color: "var(--mobile-color-txt)",
                }}
              >
                {"Hanging protocol"}
              </span>
            </>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <CustomToolTipHost
            delay={TooltipDelay.zero}
            id={"hanging-protocol"}
            directionalHint={DirectionalHint.rightCenter}
            styles={toolTipStyles}
            content={
              isHPApplied
                ? t("viewer.tools.exit-hanging-protocol")
                : t("viewer.tools.hanging-protocol")
            }
            className={"myToolTip"}
          >
            <div
              className={`highlight-tool hp-tool-split${isHPApplied ? " hp-tool-split-active" : ""}`}
              style={{
                width: "47px",
                minWidth: "47px",
                flexShrink: 0,
              }}
            >
              <div
                style={toolBarFlex}
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseLeave}
                onClick={handleHpIconClick}
              >
                <HPIcon
                  color={
                    hovered || isHPApplied ? theme.palette.activeTool : theme.palette.inactiveTool
                  }
                />
              </div>
              <div
                style={{
                  marginLeft: "10px",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
                // Always opens. The menu itself says whether anything matched —
                // a control that silently refuses to open reads as broken.
                onClick={() => setShowDropDown(!showDropDown)}
                onMouseOver={() => setDropDownHovered(true)}
                onMouseLeave={() => setDropDownHovered(false)}
              >
                <DropDown
                  color={
                    dropDownHovered ? theme.palette.activeTool : theme.palette.inactiveTool
                  }
                />
              </div>
            </div>
          </CustomToolTipHost>
        </div>
      )}
      {showDropDown && (
        <div
          className={
            isMobile
              ? "dropdow-bottom-position viewer-tool-more-dropdown hp-menu"
              : "dropdow-top-position viewer-tool-more-dropdown hp-menu"
          }
          ref={dropDownRef}
          style={{ position: "absolute", zIndex: "999" }}
        >
          <HangingProtocolMenu
            candidates={hpCandidates}
            appliedKey={isHPApplied ? hpKey(appliedHp) : null}
            onSelect={onHpClick}
            onReset={handleReset}
            onManage={handleManage}
            resetDisabled={!isHPApplied}
          />
        </div>
      )}
    </>
  );
};

export default HPTool;
